import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  getColor,
  Icons,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import RevisionTag from "../RevisionTag";
import utilsDocs from "../../shared/utils/docs";
import utilsCommon from "../../shared/utils/common";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { DocDetailDto, LogItem } from "../../types/files";
import PolicyToolTip from "../PolicyToolTip";
import { handleDocDownload } from "../../shared/utils/api";
import { useTranslation } from "react-i18next";
interface DocAsideProps {
  documentInfo?: DocDetailDto;
}

const DocLogHistory: React.FC<DocAsideProps> = ({ documentInfo }) => {
  /** Hook 정리 */ const toast = useToast();
  const { t } = useTranslation();
  const { getContentDownload } = useDocumentStore();
  const showLogCheckbox = useCheckboxGroup<number>();

  /** State 정리 */

  /** 함수 정리 */
  /*
  파일 이력 관련 로그
  logType 이 document:
    - actionTypeDbValue sign: 의견 등록
    - actionTypeDbValue unwrap: Wrapping 해제
    - actionTypeDbValue revision: 리비전 완료
    - actionTypeDbValue view: 열람
    - actionTypeDbValue download: 다운로드
    - actionTypeDbValue succession: 파일 관리자 승계
    - actionTypeDbValue change: 파일명 수정
    2,랑 4 해야함
  logType이 documentRole
    - targetType 이 User일 경우
      -- policyCode 이 DENY_PERMISSION 일 경우, 권한 회수로 표시
      -- add나 change는 모두 수정으로 표시
    - targetType 이 User이 아닌 경우
      -- actionTypeDbValue change: 기본 권한 수정
      -- actionTypeDbValue add:: 기본 권한 부여
      -- actionTypeDbValue delete: 기본 권한 삭제
 */
  const getLogTitle = (log: LogItem) => {
    const userName =
      !log.userName || log.userName === "#NONE_USER_NAME#"
        ? t("common.unAuthUser")
        : log.userName;
    if (log.logTypeDbValue === "document")
      switch (log.actionTypeDbValue) {
        case "succession":
          return t("file.timeline.save2", {
            name: userName,
            newMaster: log.logData,
          });
        case "change":
          return t("file.timeline.save4", {
            name: userName,
            docName: log.logData,
          });
        case "sign":
          return t("file.timeline.save5", {
            name: userName,
            memo: log.logData ?? "",
          });
        case "unwrap":
          return t("file.timeline.save3", {
            name: userName,
          });
        case "revision":
          return t("file.timeline.save7", {
            name: userName,
          });
        case "view":
          return t("file.timeline.save6", {
            name: userName,
          });
        case "download":
          return t("file.timeline.save8", {
            name: userName,
          });
        case "add":
          return t("file.timeline.save9", {
            name: userName,
          });
      }
    else if (
      log.policyLogs &&
      log.logTypeDbValue === "documentRole" &&
      ["change", "add", "delete"].includes(log.actionTypeDbValue)
    ) {
      const targetPolicy = log.policyLogs[0].permissionPolicy;
      if (targetPolicy.targetType === "USER") {
        return t("file.timeline.save1.changeTo", {
          name: userName,
          reciever: log.policyLogs
            .map((item) => item.permissionPolicy.targetValue)
            .join(", "),
        });
      } else {
        switch (log.actionTypeDbValue) {
          case "change":
            return t("file.timeline.save1.changeDefault", {
              name: userName,
            });
          case "add":
            return t("file.timeline.save1.createDefault", {
              name: userName,
            });
          case "delete":
            return t("file.timeline.save1.deleteDefault", {
              name: userName,
            });
        }
      }
    }
  };
  return (
    <Box
      direction="column"
      classes="mg mt-8 docAside-history"
      style={{ overflow: "auto" }}
    >
      {documentInfo?.timeline?.timelineList?.map((item) => (
        <>
          <Box classes="docAside-historyItem revision">
            <RevisionTag revisionNo={item.revisionMap.no} size={"md"} />
            <Box
              classes={"mg ml-8"}
              direction="column"
              width={"calc(100% - 40px)"}
            >
              <Box alignItems={"center"}>
                <Typography
                  type="body2_md"
                  ellipsis
                  whiteSpace="pre"
                  fontWeight={700}
                >
                  {item.revisionMap.checkinUser.userName}
                </Typography>
                <Typography classes="mg ml-4" type="body2_rg" whiteSpace="pre">
                  {t("file.versionUpdate")}
                </Typography>
              </Box>
              <Box classes="mg mt-2">
                <Typography
                  type="body4_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {utilsDocs.fileSizeFormat(item.revisionMap.fileSize)}
                </Typography>
                <Typography
                  type="body4_rg"
                  classes="mg ml-4"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {" • " +
                    utilsCommon.renderDateFormat(item.revisionMap.regDate)}
                </Typography>
              </Box>

              <Box classes="docAside-actions">
                {documentInfo?.documentInfo?.myPolicy?.uniqueId !==
                  "ONLYVIEW" && (
                  <Tooltip message={t("file.action.download")}>
                    <Button
                      btnStyle="icon"
                      size={"sm"}
                      onClick={() =>
                        handleDocDownload(
                          documentInfo.documentInfo.syncId,
                          getContentDownload,
                          toast,
                          t,
                          item.revisionMap.no + ""
                        )
                      }
                      leadingIcon={<Icons variant="line" label="download" />}
                    />
                  </Tooltip>
                )}
                <Button
                  btnStyle="icon"
                  size={"sm"}
                  onClick={() =>
                    showLogCheckbox.handleClick(item.revisionMap.no)
                  }
                  leadingIcon={
                    <Icons
                      variant="line"
                      label="chevron"
                      direction={
                        showLogCheckbox.isSelected(item.revisionMap.no)
                          ? "up"
                          : "down"
                      }
                    />
                  }
                  classes="mg ml-4"
                />
              </Box>
            </Box>
          </Box>
          {showLogCheckbox.isSelected(item.revisionMap.no) &&
            item.log.map((item1: LogItem, index1) => (
              <Box classes="docAside-historyItem log">
                {index1 === 0 ? (
                  <Box minWidth={20}>
                    <Icons
                      variant="line"
                      label="downRight"
                      style={{ minWidth: "20px" }}
                      stroke={getColor("COLOR_ICON_INTERACTIVE_SECONDARY")}
                    />
                  </Box>
                ) : (
                  <Box minWidth={20} classes={"mg mt-4"} />
                )}

                <Avatar
                  name={
                    !item1.userName || item1.userName === "#NONE_USER_NAME#"
                      ? t("common.unAuthUser")
                      : item1.userName
                  }
                  uniqueCode={utilsCommon.getUserColor(
                    item1?.userType,
                    item1?.status ?? undefined,
                    item1?.userName
                  )}
                  icon={item1.userName ? undefined : "user"}
                  size="32"
                  classes="mg ml-4"
                />
                <Box
                  classes={"mg ml-8"}
                  direction="column"
                  width={"calc(100% - 40px)"}
                >
                  <Box alignItems={"center"}>
                    <Typography type="body2_rg" wordBreak="break-word">
                      {item1.policyLogs &&
                      item1.policyLogs.length === 1 &&
                      (item1.workgroupId !== "NONEWORKGROUP" ||
                        "delete" !== item1.actionTypeDbValue) ? (
                        <PolicyToolTip
                          policy={item1.policyLogs[0].permissionPolicy}
                          targetAuth={
                            ["delete"].includes(item1.actionTypeDbValue) ||
                            (item1.actionTypeDbValue === "add" &&
                              item1.policyLogs[0].permissionPolicy
                                .targetType === "WORKGROUP")
                              ? "WORKGROUP"
                              : item1.policyLogs[0].permissionPolicy
                                  .policyCode === "DENY_PERMISSION"
                              ? "NONE"
                              : "IND"
                          }
                          docInfo={documentInfo?.documentInfo}
                          showTooltip
                        >
                          {getLogTitle(item1)}
                        </PolicyToolTip>
                      ) : (
                        getLogTitle(item1)
                      )}
                    </Typography>
                  </Box>
                  <Typography
                    type="body4_rg"
                    classes="mg mt-2"
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  >
                    {utilsCommon.renderDateFormat(item1.logDate)}
                  </Typography>
                </Box>
              </Box>
            ))}
        </>
      ))}
    </Box>
  );
};
export default DocLogHistory;
